import type { BuilderBlockSource, LibraryBlockDef, SmartFieldDef, SmartFieldType } from '@elva/ui';
import type { FormBlockCatalogResponse } from '../api/types';
import { flattenBlockCatalog } from './formsAdapters';
import { getCatalogAllowedConfigKeys } from './formsBlockConfig';

/**
 * W14/D41 — turn the served block catalog into the builder's `LibraryBlockDef[]`.
 *
 * This is the single source that replaces the deleted static local library. The
 * icon and accent are deliberately left unset so the existing id-based fallbacks
 * (`resolveFormBuilderBlockIcon`, `accentFor`) apply — the server's `icon`/`color`
 * strings are not builder icon keys / CSS vars. `mapping_available` is carried per
 * field for W16's authoring label.
 */

function sourceFromKind(kind: string | undefined): BuilderBlockSource {
  if (kind === 'smart_block') return 'smart';
  if (kind === 'patient_info') return 'patient';
  return 'basic';
}

function toStringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const items = value.filter((item): item is string => typeof item === 'string');
  return items.length > 0 ? items : undefined;
}

function toSmartField(raw: unknown): SmartFieldDef | null {
  if (raw === null || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'string' ? r.id : '';
  if (!id) return null;

  const options = toStringArray(r.options);
  const prefill =
    typeof r.prefill === 'string' ? r.prefill : toStringArray(r.prefill);

  return {
    id,
    label: typeof r.label === 'string' ? r.label : id,
    ...(typeof r.type === 'string' ? { type: r.type as SmartFieldType } : {}),
    ...(typeof r.required === 'boolean' ? { required: r.required } : {}),
    ...(typeof r.locked === 'boolean' ? { locked: r.locked } : {}),
    ...(typeof r.hint === 'string' ? { hint: r.hint } : {}),
    ...(typeof r.content === 'string' ? { content: r.content } : {}),
    ...(options ? { options } : {}),
    ...(prefill !== undefined ? { prefill } : {}),
    ...(typeof r.read_only === 'boolean' ? { readOnly: r.read_only } : {}),
    ...(typeof r.mapping_available === 'boolean' ? { mappingAvailable: r.mapping_available } : {}),
  };
}

export function catalogToLibraryBlocks(response: FormBlockCatalogResponse): LibraryBlockDef[] {
  return flattenBlockCatalog(response).map((entry): LibraryBlockDef => {
    const fields = Array.isArray(entry.fields)
      ? entry.fields.map(toSmartField).filter((field): field is SmartFieldDef => field !== null)
      : [];
    const configKeys = [...getCatalogAllowedConfigKeys(entry)];

    return {
      id: entry.type,
      label: entry.label,
      desc: entry.desc ?? '',
      source: sourceFromKind(entry.block_kind),
      ...(entry.category ? { category: entry.category } : {}),
      ...(entry.compliance ? { compliance: entry.compliance } : {}),
      ...(entry.has_consent ? { hasConsent: entry.has_consent } : {}),
      ...(fields.length > 0 ? { fields } : {}),
      ...(configKeys.length > 0 ? { configKeys } : {}),
      ...(entry.field_type ? { fieldType: entry.field_type } : {}),
      ...(entry.options?.length ? { options: entry.options } : {}),
    };
  });
}
